// "Say hi to your team" → "Start a Conversation" tutorial flow (native).
// TaskDetail's Start Chat button writes a pending payload before navigating to
// the 1:1 chat; MyChatDetail consumes it to auto-focus the input, show the
// one-time tooltip, and surface the first-message success label.

export const START_CONVERSATION_PENDING_KEY =
  'pending_start_conversation_tooltip';
// Shared with the web ChatDetail flow — both gate the same
// "Start a Conversation" tooltip.
export const START_CONVERSATION_SEEN_KEY = 'assignTooltipSeen';
// Web: set in localStorage when a user registers from any invitation link.
// The web ChatDetail shows the "Start a Conversation" tooltip once, the first
// time Task Details opens while a chat is open, and clears the flag only when
// the tooltip actually shows (TA-10892 #27).
export const INVITE_SIGNUP_START_CONVERSATION_KEY =
  'invite_signup_start_conversation_tooltip';
// Web: set in localStorage when a user registers from any invitation link.
// Only these invitees get the "You have tasks waiting" tooltip on the project
// Tasks tab; dismissing it (tapping a task) clears the flag (TA-10892 #26).
export const INVITE_SIGNUP_TASKS_WAITING_KEY = 'invite_signup_tasks_waiting';

export type StartConversationPending = {
  name: string;
  userId: string;
  chatId?: string;
  // Delay before the tooltip appears (task-assigned handoff waits for the
  // TASK ASSIGNED card to land in the chat first).
  tooltipDelayMs?: number;
};

// Per design: the "Start a Conversation" tooltip appears 2s after the user
// lands back in the chat with the TASK ASSIGNED card.
export const TASK_ASSIGNED_TOOLTIP_DELAY_MS = 2000;

/**
 * Build the Start Chat handoff written after a task is assigned from a chat
 * (member-joined flow). Returns null when there is no chat to return to or
 * the task was only self-assigned — no one new to start a conversation with.
 */
export const buildTaskAssignedStartConversationPending = ({
  members,
  meId,
  chatId,
}: {
  members: ReadonlyArray<{ id?: string | null; name?: string | null }>;
  meId?: string | null;
  chatId?: string | null;
}): StartConversationPending | null => {
  if (!chatId) return null;
  const assignee = members.find((m) => !!m.id && m.id !== meId);
  if (!assignee?.id || !assignee.name) return null;
  return {
    name: assignee.name,
    userId: assignee.id,
    chatId,
    tooltipDelayMs: TASK_ASSIGNED_TOOLTIP_DELAY_MS,
  };
};

export const parseStartConversationPending = (
  raw: string | null | undefined
): StartConversationPending | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StartConversationPending;
    if (!parsed?.name || !parsed?.userId) return null;
    return parsed;
  } catch {
    return null;
  }
};

// NOTE: process.env.SUPPORT_USER_ID is inlined by babel
// (transform-inline-environment-variables); the parameter exists so tests can
// inject a value.
export const isSupportUser = (
  user?: { id?: string | null; name?: string | null } | null,
  supportUserId: string | undefined = process.env.SUPPORT_USER_ID
): boolean =>
  (!!supportUserId && user?.id === supportUserId) ||
  user?.name === 'TaskTag Support';

export const firstNameOf = (name?: string | null): string =>
  name?.split(' ')[0] ?? '';

type ResolveArgs = {
  pending: StartConversationPending | null;
  alreadySeen: boolean;
  chatId: string;
  isGroupChat: boolean;
  isHelpdeskChat: boolean;
  // The user has already sent their first chat — the onboarding tooltip is
  // no longer relevant, so suppress it (focus-only handoff still applies).
  firstChatSent: boolean;
};

/**
 * Decide what the chat screen should do with a pending Start Chat handoff.
 * Focus follows the handoff even when the tooltip was already seen; the
 * tooltip itself is strictly one-time. A handoff pinned to this chat can
 * never be consumed by another screen, so it is cleared even when the chat
 * type makes it ineligible (group / helpdesk) — otherwise it would sit in
 * storage forever.
 */
export const resolveStartConversationFlow = ({
  pending,
  alreadySeen,
  chatId,
  isGroupChat,
  isHelpdeskChat,
  firstChatSent,
}: ResolveArgs): {
  shouldFocusInput: boolean;
  shouldShowTooltip: boolean;
  shouldClearPending: boolean;
} => {
  const targetsThisChat = !pending?.chatId || pending.chatId === chatId;
  const skip = !pending || isGroupChat || isHelpdeskChat || !targetsThisChat;

  if (skip) {
    return {
      shouldFocusInput: false,
      shouldShowTooltip: false,
      // Only an explicit pin to this chat is safe to drop — an un-pinned
      // legacy handoff may still belong to a chat screen we haven't visited.
      shouldClearPending: !!pending?.chatId && pending.chatId === chatId,
    };
  }
  return {
    shouldFocusInput: true,
    shouldShowTooltip: !alreadySeen && !firstChatSent,
    shouldClearPending: true,
  };
};
